import {
  CompanySearch,
  ExportHistoryParams,
  TrendingTopicsParams,
} from '@/services/call-log-service/call-log-service.types';
import { AnnotationContext } from '@/services/conversations-service/conversations-service.types';
import {
  CallMetricsChartParams,
  DashboardFilters,
  SmsMetricsChartParams,
} from '@/services/dashboard-service/dashboard.service.types';
import { DocumentsFilters } from '@/services/documents-service/documents-service.types';
import { MaintenancesFilters } from '@/services/maintenances-service/maintenances-service.types';
import { OutboundCallsParams } from '@/services/outbound-calls-service/calls.service.types';
import { CampaignEntriesParams } from '@/services/outbound-calls-service/campaigns.service.types';
import { SurveysFilters } from '@/services/surveys-service/surveys-service.types';
import { WebchatHistoryParams } from '@/services/webchat-service/webchat.service.types';

export const ReactQueryKeys = {
  USER: () => ['user'],
  AI_PERFORMANCE: (params: DashboardFilters) => ['ai-performance', params],
  CALL_METRICS: (params: DashboardFilters) => ['call-metrics', params],
  CALLBACK_QUEUE: (params: DashboardFilters) => ['callback-queue', params],
  METRICS_CHART: (params: CallMetricsChartParams) => ['metrics-chart', params],
  SMS_METRICS: (params: DashboardFilters) => ['sms-metrics', params],
  SMS_METRICS_CHART: (params: SmsMetricsChartParams) => ['sms-metrics-chart', params],
  CALL_LOG: (params: DashboardFilters) => ['call-log', params],
  COMPANY_SEARCH: (params: CompanySearch) => ['company-search', params],
  CONVERSATIONS: (params: AnnotationContext) => ['conversations', params],
  CONVERSATIONS_REVIEWED: (params: AnnotationContext) => ['conversations-reviewed', params],
  INSIGHTS: (params: DashboardFilters) => ['insights', params],
  SMS_LOG: (params: DashboardFilters) => ['sms-log', params],
  EXPORT_HISTORY: (params: ExportHistoryParams) => ['export-history', params],
  EXPORT_HISTORY_CVS: () => ['export-history-csv'],
  EXPORT_CALL_LOGS: () => ['export-call-logs'],
  TRENDING_TOPICS: (params: TrendingTopicsParams) => ['trending-topics', params],
  SURVEYS: (params: SurveysFilters) => ['surveys', params],
  MAINTENANCES: (params: MaintenancesFilters) => ['maintenances', params],
  MAINTENANCE: (id: number) => ['maintenance', id],
  DOCUMENTS: (params: DocumentsFilters) => ['documents', params],
  DOCUMENT: (id: string) => ['document', id],
  // Prefixes, for invalidating every cached page or every cached document. Not query keys.
  DOCUMENTS_ALL: () => ['documents'],
  DOCUMENT_ALL: () => ['document'],
  INIT_MESS: (companyName?: string) => ['company-message', companyName],
  AFTER_HOURS_SETTINGS: (companyName?: string) => ['after-hours-settings', companyName],
  COMPANIES: () => ['companies'],
  BUSINESS_HOURS: (company_id?: string, date?: string) => ['business-hours', company_id, date],
  REDIRECTION_TARGETS: (company_id?: number) => ['redirection-targets', company_id],
  REDIRECTION_TARGET: (id: number) => ['redirection-target', id],
  RECORDING_URL: (conversationId?: number, category?: string) =>
    category ? ['recording-url', conversationId, category] : ['recording-url', conversationId],
  INCOMING_NUMBERS: (companyId?: number) => ['incoming-numbers', companyId],
  AGENTS: (companyId?: number) => ['agents', companyId],
  AGENT: (id: string) => ['agent', id],
  AGENT_USAGE: (id: string) => ['agent-usage', id],
  AGENT_PREFILL: (companyId?: number) => ['agent-prefill', companyId],
  AGENT_TRANSFERABLE_DOCS: (id: string, companyId?: number) => ['agent-transferable-docs', id, companyId],
  AGENT_TRANSFER_ANNOUNCEMENTS: (id: string, companyId?: number) => ['agent-transfer-announcements', id, companyId],
  AGENT_FILLER_CLIPS: (id: string, companyId?: number) => ['agent-filler-clips', id, companyId],
  AGENT_AUDIO_CLIP_TEXTS: (id: string, companyId?: number) => ['agent-audio-clip-texts', id, companyId],
  AGENT_VOICE_HEALTH: (id: string, companyId?: number) => ['agent-voice-health', id, companyId],
  USER_PROFILE: (userId?: number) => ['user-profile', userId],
  USER_PROFILE_DATA: (userId?: number) => ['user-profile-data', userId],
  USER_CALL_HISTORY: (userId?: number) => ['user-call-history', userId],
  LANGUAGE_VOICES: (companyId?: number) => ['language-voices', companyId],
  VOICES: (companyId?: number, search?: string, category?: string, voiceType?: string) => [
    'voices',
    companyId,
    search,
    category,
    voiceType,
  ],
  SHARED_VOICES: (companyId?: number, search?: string, language?: string, accent?: string, gender?: string) => [
    'shared-voices',
    companyId,
    search,
    language,
    accent,
    gender,
  ],
  WEBCHAT_CONVERSATIONS: () => ['webchat-conversations'],
  WEBCHAT_MESSAGES: (id: number) => ['webchat-messages', id],
  WEBCHAT_HISTORY: (params: WebchatHistoryParams) => ['webchat-history', params],
  WEBCHAT_QUEUE_STATS: () => ['webchat-queue-stats'],
  WEBCHAT_AVAILABLE_AGENTS: () => ['webchat-available-agents'],
  OUTBOUND_SCENARIOS: (companyId?: number) => ['outbound-scenarios', companyId],
  OUTBOUND_CALLS: (params: OutboundCallsParams) => ['outbound-calls', params],
  OUTBOUND_CALL: (id: number) => ['outbound-call', id],
  OUTBOUND_CAMPAIGNS: (companyId?: number) => ['outbound-campaigns', companyId],
  OUTBOUND_CAMPAIGN: (id: number) => ['outbound-campaign', id],
  OUTBOUND_CAMPAIGN_ENTRIES: (id: number, params?: CampaignEntriesParams) =>
    params ? ['outbound-campaign-entries', id, params] : ['outbound-campaign-entries', id],
  OUTBOUND_LEAD: (id: number) => ['outbound-lead', id],
  SALESPEOPLE: (companyId?: number) => ['salespeople', companyId],
  // Prefixes, for invalidating every cached company or filter. Not query keys.
  OUTBOUND_SCENARIOS_ALL: () => ['outbound-scenarios'],
  OUTBOUND_CALLS_ALL: () => ['outbound-calls'],
  OUTBOUND_CAMPAIGNS_ALL: () => ['outbound-campaigns'],
  OUTBOUND_CAMPAIGN_ENTRIES_ALL: () => ['outbound-campaign-entries'],
  SALESPEOPLE_ALL: () => ['salespeople'],
} as const;
